/**
 * Varejo · Curva ABC (issue #242) — ranking de clientes por valor CONTRATADO.
 *
 * Contratado = Σ|valor| de VENDAS por mês de FORNECIMENTO na janela; classes
 * pelo % acumulado incluindo a linha (≤80 A, ≤95 B, resto C). NÃO é o
 * "faturamento" da planilha da Mesa: o gate do PR #243 provou que a planilha
 * mede faturamento REALIZADO (invoices, fonte que o Cortex não ingere em
 * prod) e o Zeus esvazia o valor dos trades ao faturar — métricas distintas,
 * rotulamos "contratado" pra não confundir. Filtro de book (#60c); export
 * CSV/XLSX com aba extra "Clientes Mensal".
 */
import { useMemo, useState } from "react";
import { Loading } from "../Loading";
import { ExportDropdown, type ExportColumn } from "../ui/ExportDropdown";
import { useVarejoCurvaAbc } from "../../hooks/useVarejo";
import { fmtBRL, fmtPct } from "../../utils/formatters";

const BOOKS = [
  { id: "varejo", label: "Varejo" },
  { id: "atacado", label: "Atacado" },
  { id: "todos", label: "Todos" },
] as const;

const CLASSE_CLS: Record<string, string> = {
  A: "bg-indra-positivo/20 text-indra-positivo",
  B: "bg-amber-400/20 text-amber-300",
  C: "bg-white/10 text-white/50",
};

function mesInputToIso(v: string): string | undefined {
  return v ? `${v}-01` : undefined;
}

export function CurvaAbcTab() {
  const [book, setBook] = useState<string>("varejo");
  const [deMes, setDeMes] = useState("");   // "YYYY-MM" (input month)
  const [ateMes, setAteMes] = useState("");
  const { data, isLoading, error } = useVarejoCurvaAbc({
    book,
    de: mesInputToIso(deMes),
    ate: mesInputToIso(ateMes),
  });

  const mesesUnicos = useMemo(() => {
    if (!data) return [];
    const s = new Set<string>();
    for (const l of data.linhas) for (const m of l.meses ?? []) s.add(m.mes);
    return [...s].sort();
  }, [data]);

  const mensalRows = useMemo(() => {
    if (!data) return [];
    return data.linhas.map((l) => {
      const row: Record<string, unknown> = { cliente: l.cliente, cnpj: l.cnpj ?? "" };
      for (const m of l.meses ?? []) row[m.mes] = m.valor;
      return row;
    });
  }, [data]);

  const exportCols: ExportColumn<Record<string, unknown>>[] = [
    { key: "posicao", header: "#" },
    { key: "cliente", header: "Cliente" },
    { key: "cnpj", header: "CNPJ" },
    { key: "valor", header: "Contratado (R$)" },
    { key: "mwh", header: "MWh" },
    { key: "n_trades", header: "Trades" },
    { key: "pct", header: "% Part." },
    { key: "pct_acumulado", header: "% Acum." },
    { key: "classe", header: "Classe" },
  ];
  const mensalCols: ExportColumn<Record<string, unknown>>[] = [
    { key: "cliente", header: "Cliente" },
    { key: "cnpj", header: "CNPJ" },
    ...mesesUnicos.map((m) => ({ key: m, header: m.slice(0, 7) })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">Book</div>
          <div className="flex gap-1" data-testid="abc-book-filtro">
            {BOOKS.map((b) => (
              <button
                key={b.id}
                onClick={() => setBook(b.id)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  book === b.id
                    ? "border-indra-turquesa/60 bg-indra-turquesa-muted text-indra-turquesa"
                    : "border-white/10 text-white/60 hover:bg-white/5"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
        <label className="text-xs text-white/60">
          De
          <input
            type="month"
            value={deMes}
            onChange={(e) => setDeMes(e.target.value)}
            className="block mt-1 bg-indra-azul-surface border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs"
          />
        </label>
        <label className="text-xs text-white/60">
          Até
          <input
            type="month"
            value={ateMes}
            onChange={(e) => setAteMes(e.target.value)}
            className="block mt-1 bg-indra-azul-surface border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs"
          />
        </label>
        <div className="text-[11px] text-white/40 pb-1.5">
          {data
            ? `${data.metadados.de.slice(0, 7)} → ${data.metadados.ate.slice(0, 7)} · ${data.metadados.n_clientes} clientes · ${fmtBRL(data.metadados.total_valor, { decimals: 0 })}`
            : "janela default: últimos 12 meses"}
        </div>
        <div className="ml-auto">
          {data && (
            <ExportDropdown
              filename={`curva-abc-${book}`}
              columns={exportCols}
              rows={data.linhas as unknown as Record<string, unknown>[]}
              primarySheetName="Curva ABC"
              extraSheets={[
                { name: "Clientes Mensal", columns: mensalCols, rows: mensalRows },
              ]}
            />
          )}
        </div>
      </div>

      {isLoading && !data && <Loading label="Carregando Curva ABC…" />}
      {error && (
        <div className="text-sm text-indra-negativo">
          Falha ao carregar a Curva ABC — tente Atualizar.
        </div>
      )}

      {data && (
        <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="abc-tabela">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700/40">
                <th className="py-2 pr-2">#</th>
                <th className="py-2 pr-2">Cliente</th>
                <th className="py-2 pr-2 text-right">Contratado</th>
                <th className="py-2 pr-2 text-right">MWh</th>
                <th className="py-2 pr-2 text-right">% Part.</th>
                <th className="py-2 pr-2 w-44">Participação</th>
                <th className="py-2 pr-2 text-right">% Acum.</th>
                <th className="py-2">Classe</th>
              </tr>
            </thead>
            <tbody>
              {data.linhas.map((l) => (
                <tr
                  key={`${l.id_unidade_contraparte ?? "null"}`}
                  className="border-b border-slate-700/20 hover:bg-white/5"
                >
                  <td className="py-1.5 pr-2 text-white/40">{l.posicao}</td>
                  <td className="py-1.5 pr-2">
                    {l.cliente}
                    {l.cnpj && (
                      <span className="text-white/30 text-[11px] ml-2 font-mono">{l.cnpj}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono">
                    {fmtBRL(l.valor, { decimals: 0 })}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono">
                    {l.mwh.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtPct(l.pct)}</td>
                  <td className="py-1.5 pr-2">
                    <div className="h-2 rounded bg-white/5">
                      <div
                        className="h-2 rounded bg-indra-turquesa/70"
                        style={{ width: `${Math.min(100, l.pct)}%` }}
                      />
                    </div>
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono">{fmtPct(l.pct_acumulado)}</td>
                  <td className="py-1.5">
                    <span
                      className={`px-2 py-0.5 rounded text-[11px] font-bold ${CLASSE_CLS[l.classe] ?? ""}`}
                    >
                      {l.classe}
                    </span>
                  </td>
                </tr>
              ))}
              {data.linhas.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-3 text-white/40">
                    Sem vendas contratadas na janela selecionada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
